import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { SubmitButtonsFormValues } from '../SubmitButtons';
import { DocumentFieldValueModel } from 'components/Forms/FormComponents/DocumentsField';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { customersAndProductsValidationSchema } from 'validations/intake-form/intake-form-steps';
import { TECHNOLOGY_AND_SOFTWARE_DEVELOPMENT_INDUSTRY_CODE } from 'constants/global';
import {
  AmountOptions,
  CustomerOptions,
  SourceOfRevenueOptions,
  SubIndustryOptions,
  VirtualAssetInvolvementOptions,
} from 'enums/onboarding/crm';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { Form, SectionIntro } from '@ui';

export interface FormValuesModel extends SubmitButtonsFormValues {
  industryCodes: string[];
  subIndustries: SubIndustryOptions[];
  isBlockchainTechnologyUsed: boolean | null;
  isVirtualAssetsInvolvedInBusiness: boolean | null;
  involvedVirtualAssets: VirtualAssetInvolvementOptions[];
  customers: CustomerOptions[];
  customerOtherDetails: string;
  sourceOfRevenue: SourceOfRevenueOptions[];
  sourceOfRevenueOtherDetails: string;
  businessInfo: string;
  supportingDocuments: DocumentFieldValueModel[];
  projectedRevenueInSixMonths: AmountOptions | null;
  projectedRevenueInSixMonthsAmount: number | null;
  projectedRevenueInOneYear: AmountOptions | null;
  projectedRevenueInOneYearAmount: number | null;
  projectedRevenueInTwoYears: AmountOptions | null;
  projectedRevenueInTwoYearsAmount: number | null;
}

interface IProps {
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
}

const CustomersAndProducts = ({
  disabled,
  renderNavigateButtonInTitle,
}: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const {
    intakeForm,
    navigateToNextStep,
    navigateToPreviousStep,
    updateIntakeFormFields,
  } = useEntityIntakeFormContext();

  const initialValues = useMemo<FormValuesModel | null>(() => {
    if (!intakeForm) {
      return null;
    }
    const intakeFormFields = intakeForm.fields;

    const supportingDocuments: DocumentFieldValueModel[] = Array.isArray(
      intakeFormFields.supportingDocuments,
    )
      ? intakeFormFields.supportingDocuments
          .map((e) => {
            const document = intakeForm.metadata.documents[e];
            if (document) {
              return {
                id: document.id as string,
                name: document.files[0].name as string,
                fileId: document.files[0].id,
                file: null,
              };
            }
          })
          .filter((e) => !!e)
      : [];

    return {
      submitType: null,
      industryCodes: intakeFormFields.industryCodes || [],
      subIndustries:
        Array.isArray(intakeFormFields.subIndustries) &&
        intakeFormFields.subIndustries.length
          ? intakeFormFields.subIndustries
          : [],
      isBlockchainTechnologyUsed:
        typeof intakeFormFields.isBlockchainTechnologyUsed === 'boolean'
          ? !!intakeFormFields.isBlockchainTechnologyUsed
          : null,
      isVirtualAssetsInvolvedInBusiness:
        typeof intakeFormFields.isVirtualAssetsInvolvedInBusiness === 'boolean'
          ? !!intakeFormFields.isVirtualAssetsInvolvedInBusiness
          : null,
      involvedVirtualAssets: Array.isArray(
        intakeFormFields.involvedVirtualAssets,
      )
        ? intakeFormFields.involvedVirtualAssets
        : [],
      customers: intakeFormFields.customerGroups || [],
      customerOtherDetails: intakeFormFields.customerGroupOtherDetails || '',
      sourceOfRevenue: intakeFormFields.sourcesOfRevenue || [],
      sourceOfRevenueOtherDetails:
        intakeFormFields.sourceOfRevenueOtherDetails || '',
      businessInfo: intakeFormFields.businessDescription || '',
      supportingDocuments: supportingDocuments,
      projectedRevenueInSixMonths: intakeFormFields.projectedRevenue6m || null,
      projectedRevenueInSixMonthsAmount:
        intakeFormFields.projectedRevenue6mAmount || null,
      projectedRevenueInOneYear: intakeFormFields.projectedRevenue12m || null,
      projectedRevenueInOneYearAmount:
        intakeFormFields.projectedRevenue12mAmount || null,
      projectedRevenueInTwoYears: intakeFormFields.projectedRevenue24m || null,
      projectedRevenueInTwoYearsAmount:
        intakeFormFields.projectedRevenue24mAmount || null,
    };
  }, [intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    if (!intakeForm) {
      return;
    }
    await updateIntakeFormFields(
      {
        industryCodes: values.industryCodes.length
          ? values.industryCodes
          : null,
        subIndustries: values.industryCodes.length
          ? values.subIndustries.length
            ? values.subIndustries
            : null
          : null,
        isBlockchainTechnologyUsed: values.industryCodes.includes(
          TECHNOLOGY_AND_SOFTWARE_DEVELOPMENT_INDUSTRY_CODE,
        )
          ? typeof values.isBlockchainTechnologyUsed === 'boolean'
            ? values.isBlockchainTechnologyUsed
            : null
          : null,
        isVirtualAssetsInvolvedInBusiness:
          typeof values.isVirtualAssetsInvolvedInBusiness === 'boolean'
            ? values.isVirtualAssetsInvolvedInBusiness
            : null,
        involvedVirtualAssets:
          values.isVirtualAssetsInvolvedInBusiness &&
          values.involvedVirtualAssets.length
            ? values.involvedVirtualAssets
            : null,
        customerGroups: values.customers.length ? values.customers : null,
        customerGroupOtherDetails:
          values.customers.length &&
          values.customers.includes(CustomerOptions.Other)
            ? values.customerOtherDetails || null
            : null,
        sourcesOfRevenue: values.sourceOfRevenue.length
          ? values.sourceOfRevenue
          : null,
        sourceOfRevenueOtherDetails:
          values.sourceOfRevenue.length &&
          values.sourceOfRevenue.includes(SourceOfRevenueOptions.Other)
            ? values.sourceOfRevenueOtherDetails || null
            : null,
        businessDescription: values.businessInfo || null,
        supportingDocuments: values.supportingDocuments.length
          ? values.supportingDocuments.map((e) => e.id)
          : null,
        projectedRevenue6m: values.projectedRevenueInSixMonths || null,
        projectedRevenue6mAmount:
          values.projectedRevenueInSixMonths === AmountOptions.Above1m
            ? typeof values.projectedRevenueInSixMonthsAmount === 'number'
              ? values.projectedRevenueInSixMonthsAmount
              : null
            : null,
        projectedRevenue12m: values.projectedRevenueInOneYear || null,
        projectedRevenue12mAmount:
          values.projectedRevenueInOneYear === AmountOptions.Above1m
            ? typeof values.projectedRevenueInOneYearAmount === 'number'
              ? values.projectedRevenueInOneYearAmount
              : null
            : null,
        projectedRevenue24m: values.projectedRevenueInTwoYears || null,
        projectedRevenue24mAmount:
          values.projectedRevenueInTwoYears === AmountOptions.Above1m
            ? typeof values.projectedRevenueInTwoYearsAmount === 'number'
              ? values.projectedRevenueInTwoYearsAmount
              : null
            : null,
      },
      undefined,
    );
    switch (values.submitType) {
      case 'save-continue':
        navigateToNextStep();
        break;
      case 'back':
        navigateToPreviousStep();
        break;
    }
  };

  return (
    <>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant={disabled ? 'h5' : undefined}
        title={
          <DivAlignCenter>
            {t('intake_form.customers_and_products.section_title', {
              entityName:
                intakeForm?.fields?.entityLegalName ||
                t(
                  'intake_form.customers_and_products.entity_name_when_not_provided',
                ),
            })}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton navigateToStepKey="customers_and_products" />
            ) : null}
          </DivAlignCenter>
        }
      />
      <Form
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={<InnerForm />}
        initialValues={initialValues}
        validationSchema={customersAndProductsValidationSchema}
      />
    </>
  );
};

export default CustomersAndProducts;
